import { absoluteYearOf, eraByKey, eraOf } from '../engine/eras.js';
import { mul } from '../engine/math.js';
import { isCompiled } from '../engine/types.js';
import { monthLength, yearIsLeap } from '../engine/year.js';
import { APPROX_WORDS, KEYWORDS, nameTokens, PART_WORDS, RANGE_WORDS, tokenize, UNIT_WORDS, } from './grammar.js';
const NAME_TABLES = new WeakMap();
/** Every non-empty name, abbreviation and suffix of the calendar, as folded token lists. */
function nameTable(calendar) {
    const cached = NAME_TABLES.get(calendar);
    if (cached !== undefined)
        return cached;
    const entries = [];
    const addName = (text, kind, key) => {
        const tokens = nameTokens(text ?? '');
        if (tokens.length > 0)
            entries.push({ tokens, kind, key });
    };
    for (const m of calendar.months) {
        const kind = m.intercalary ? 'intercalary' : 'month';
        addName(m.name, kind, m.key);
        addName(m.abbreviation, kind, m.key);
    }
    for (const s of calendar.seasons) {
        addName(s.name, 'season', s.key);
        addName(s.abbreviation, 'season', s.key);
    }
    for (const e of calendar.eras) {
        addName(e.name, 'era', e.key);
        addName(e.suffix, 'era', e.key);
    }
    for (const [i, w] of calendar.definition.weekdays.entries()) {
        addName(w.name, 'weekday', `${i}`);
        addName(w.abbreviation, 'weekday', `${i}`);
    }
    NAME_TABLES.set(calendar, entries);
    return entries;
}
class ParseFailure {
    reason;
    at;
    constructor(reason, at) {
        this.reason = reason;
        this.at = at;
    }
}
const DIGITS = /^[0-9]+$/;
const DECADE = /^([0-9]+)s$/;
const ORDINAL = /^([0-9]+)(?:st|nd|rd|th)$/;
const HOUR = /^([0-9]+)h$/;
const COMPACT = /^([0-9]+)([a-z]+)$/;
const DAYISH = new Set(['day', 'hour', 'minute']);
function toInt(text) {
    const n = Number(text);
    return Number.isSafeInteger(n) ? n : null;
}
class Parser {
    input;
    calendar;
    now;
    pos = 0;
    tokens;
    names;
    constructor(input, calendar, now) {
        this.input = input;
        this.calendar = calendar;
        this.now = now;
        this.tokens = tokenize(input);
        this.names = nameTable(calendar);
    }
    // ------------------------------------------------------------------------------ token helpers
    tok(offset = 0) {
        return this.tokens[this.pos + offset];
    }
    atOf(offset = 0) {
        return this.tok(offset)?.at ?? this.input.length;
    }
    fail(reason, at = this.atOf()) {
        throw new ParseFailure(reason, at);
    }
    word(offset = 0) {
        const t = this.tok(offset);
        return t?.kind === 'word' ? t.text : null;
    }
    punct(offset = 0) {
        const t = this.tok(offset);
        return t?.kind === 'punct' ? t.text : null;
    }
    matchName(offset = 0) {
        const start = this.pos + offset;
        let best = 0;
        let found = [];
        for (const entry of this.names) {
            const n = entry.tokens.length;
            if (n < best || start + n > this.tokens.length)
                continue;
            let same = true;
            for (let i = 0; i < n && same; i++) {
                const t = this.tokens[start + i];
                same = t.kind !== 'anchor' && t.text === entry.tokens[i];
            }
            if (!same)
                continue;
            if (n > best) {
                best = n;
                found = [];
            }
            found.push(entry);
        }
        if (found.length === 0)
            return { status: 'none' };
        const distinct = new Set(found.map((e) => `${e.kind}:${e.key}`));
        if (distinct.size > 1)
            return { status: 'ambiguous', length: best };
        const [entry] = found;
        if (entry.kind === 'weekday')
            return { status: 'weekday', length: best };
        return { status: 'one', kind: entry.kind, key: entry.key, length: best };
    }
    /** A grammar word at `offset` that no calendar name claims. */
    keyword(text, offset = 0) {
        return this.word(offset) === text && this.matchName(offset).status === 'none';
    }
    nameAt(match) {
        return this.fail(match.status === 'ambiguous' ? 'ambiguous_name' : 'unknown_name');
    }
    /** Fails at the current token with the reason that best describes it. */
    failHere() {
        const t = this.tok();
        if (t === undefined)
            return this.fail('unexpected_text');
        const match = this.matchName();
        if (match.status === 'ambiguous' || match.status === 'weekday')
            return this.nameAt(match);
        if (match.status === 'none' && t.kind === 'word' && !KEYWORDS.has(t.text)) {
            if (!DIGITS.test(t.text) && !COMPACT.test(t.text))
                return this.fail('unknown_name');
        }
        return this.fail('unexpected_text');
    }
    expectEnd() {
        if (this.tok() !== undefined)
            this.failHere();
    }
    number(offset = 0) {
        const w = this.word(offset);
        return w !== null && DIGITS.test(w) ? toInt(w) : null;
    }
    // ---------------------------------------------------------------------------------- phrases
    run() {
        if (this.tokens.length === 0)
            return { kind: 'invalid', reason: 'empty', at: 0 };
        if (this.keyword('undated')) {
            this.pos++;
            this.expectEnd();
            return { kind: 'undated' };
        }
        if (this.keyword('now'))
            return this.parseNow();
        if (this.keyword('session'))
            return this.parseSession();
        if (this.relativeStarts())
            return this.parseRelative();
        return this.parseDate();
    }
    parseNow() {
        this.pos++;
        if (this.tok() === undefined)
            return { kind: 'now', offset: null };
        const sign = this.punct();
        if (sign !== '+' && sign !== '-')
            this.failHere();
        this.pos++;
        const amount = this.amount();
        if (amount === null || amount.n < 1)
            this.fail('unexpected_text');
        this.pos += amount.length;
        this.expectEnd();
        return { kind: 'now', offset: { n: sign === '-' ? -amount.n : amount.n, unit: amount.unit } };
    }
    /** `2 days` or `2d` at the current token: the count, the unit and the tokens it takes. */
    amount() {
        const n = this.number();
        const unit = n === null ? undefined : UNIT_WORDS.get(this.word(1) ?? '');
        if (n !== null && unit !== undefined)
            return { n, unit, length: 2 };
        const compact = COMPACT.exec(this.word() ?? '');
        const cu = compact === null ? undefined : UNIT_WORDS.get(compact[2]);
        const cn = compact === null ? null : toInt(compact[1]);
        if (cu === undefined || cn === null)
            return null;
        return { n: cn, unit: cu, length: 1 };
    }
    parseSession() {
        this.pos++;
        const n = this.number();
        if (n === null || n < 1)
            this.failHere();
        this.pos++;
        this.expectEnd();
        return { kind: 'session', number: n };
    }
    relation() {
        for (const word of ['after', 'before', 'during']) {
            if (this.keyword(word))
                return { relation: word, length: 1 };
        }
        if (this.keyword('same') && this.word(1) === 'time' && this.word(2) === 'as') {
            return { relation: 'same_time', length: 3 };
        }
        return null;
    }
    relativeStarts() {
        if (this.matchName().status !== 'none')
            return false;
        if (this.relation() !== null)
            return true;
        if (this.keyword('at') && this.word(1) === 'least')
            return true;
        if (this.keyword('within'))
            return true;
        if (this.number() !== null || COMPACT.test(this.word() ?? '')) {
            if (this.matchName(1).status !== 'none')
                return false;
            const next = this.word(1) ?? '';
            if (this.amount() !== null)
                return true;
            if (['after', 'before', 'during', 'same'].includes(next))
                return true;
            return this.isRangeSep(1) && this.number(2) !== null && UNIT_WORDS.has(this.word(3) ?? '');
        }
        return false;
    }
    isRangeSep(offset = 0) {
        const t = this.tok(offset);
        return t !== undefined && t.kind !== 'anchor' && RANGE_WORDS.has(t.text);
    }
    parseRelative() {
        const lagAt = this.atOf();
        let lag = null;
        if (this.relation() === null) {
            let mode = 'exact';
            if (this.keyword('at') && this.word(1) === 'least') {
                mode = 'min';
                this.pos += 2;
            }
            else if (this.keyword('within')) {
                mode = 'max';
                this.pos++;
            }
            const first = this.amountOrBare();
            if (first === null)
                return this.fail('bad_lag', lagAt);
            let max = first.n;
            let unit = first.unit;
            if (unit === undefined && mode === 'exact' && this.isRangeSep()) {
                this.pos++;
                const second = this.amountOrBare();
                if (second === null)
                    return this.fail('bad_lag', lagAt);
                max = second.n;
                unit = second.unit;
            }
            if (unit === undefined || first.n < 1 || max < first.n)
                this.fail('bad_lag', lagAt);
            lag = { min: mode === 'max' ? null : first.n, max: mode === 'min' ? null : max, unit };
        }
        const rel = this.relation();
        if (rel === null)
            return this.fail('anchor_expected');
        if (lag !== null && rel.relation !== 'after' && rel.relation !== 'before') {
            this.fail('bad_lag', lagAt);
        }
        this.pos += rel.length;
        const anchor = this.tok();
        if (anchor?.kind !== 'anchor' || anchor.label === null)
            return this.fail('anchor_expected');
        this.pos++;
        this.expectEnd();
        return {
            kind: 'relative',
            relation: rel.relation,
            anchor: { label: anchor.label, fid: anchor.fid },
            lag,
        };
    }
    /** A count with its unit (`3 days`, `3d`) or a bare count (`3`, before `–5 days`). */
    amountOrBare() {
        const amount = this.amount();
        if (amount !== null) {
            this.pos += amount.length;
            return amount;
        }
        const n = this.number();
        if (n === null)
            return null;
        this.pos++;
        return { n, unit: undefined };
    }
    parseDate() {
        let qualifier = 'exact';
        const first = this.tok();
        if ((first.kind === 'word' &&
            APPROX_WORDS.has(first.text) &&
            this.matchName().status === 'none') ||
            (first.kind === 'punct' && first.text === '~')) {
            qualifier = 'approximate';
            this.pos++;
            if (first.kind === 'word' && first.text === 'c' && this.punct() === '.')
                this.pos++;
        }
        let open = null;
        if (this.keyword('until'))
            open = 'lo';
        else if (this.keyword('since'))
            open = 'hi';
        if (open !== null)
            this.pos++;
        const left = this.parseSide();
        let right = null;
        if (open === null && this.isRangeSep() && this.matchName().status === 'none') {
            this.pos++;
            right = this.parseSide();
        }
        if (this.punct() === '?') {
            if (qualifier !== 'exact')
                this.fail('unexpected_text');
            qualifier = 'uncertain';
            this.pos++;
        }
        this.expectEnd();
        return this.combine(left, right, open, qualifier);
    }
    // ------------------------------------------------------------------------------------ sides
    parseSide() {
        const at = this.atOf();
        let part = null;
        const w = this.word();
        if (w !== null && PART_WORDS.has(w) && this.matchName().status === 'none') {
            part = w;
            this.pos++;
        }
        const draft = this.sideBody(at, part !== null);
        if (part !== null)
            draft.parts.part = part;
        draft.end = this.atOf();
        return draft;
    }
    draft(at, precision, parts, hasYear) {
        return { at, end: at, precision, parts, hasYear, bare: false, interNoDay: false };
    }
    sideBody(at, parted) {
        const match = this.matchName();
        if (match.status === 'ambiguous' || match.status === 'weekday')
            this.nameAt(match);
        if (match.status === 'one') {
            const nameAt = this.atOf();
            this.pos += match.length;
            switch (match.kind) {
                case 'era': {
                    const value = this.yearValue();
                    if (value !== null)
                        return this.yearDraft(at, value, match.key);
                    if (parted)
                        this.fail('unexpected_text', nameAt);
                    return this.draft(at, 'era', { era: match.key }, true);
                }
                case 'season':
                case 'month': {
                    const year = this.yearExpr();
                    const parts = { [match.kind]: match.key, ...year };
                    return this.draft(at, match.kind, parts, year !== null);
                }
                default:
                    return this.dayDraft(at, null, match.key);
            }
        }
        if (this.keyword('week'))
            return this.weekDraft(at);
        const n = this.number();
        if (n !== null) {
            const next = this.matchName(1);
            if (next.status === 'one' && (next.kind === 'month' || next.kind === 'intercalary')) {
                const dayAt = this.atOf();
                this.pos += 1 + next.length;
                return this.dayDraft(at, { n, at: dayAt }, next.key);
            }
        }
        const value = this.yearValue();
        if (value === null)
            return this.failHere();
        const era = this.eraAfter();
        const draft = this.yearDraft(at, value, era);
        draft.bare = value.precision === 'year' && !value.signed && era === undefined;
        return draft;
    }
    yearDraft(at, value, era) {
        const parts = era === undefined ? {} : { era };
        parts.year = value.year;
        return this.draft(at, value.precision, parts, true);
    }
    /** A `-` directly before the digits of the next token, and not directly after a word. */
    signHere() {
        const t = this.tok();
        const next = this.tok(1);
        const prev = this.tok(-1);
        return (t?.kind === 'punct' &&
            t.text === '-' &&
            next?.kind === 'word' &&
            next.at === t.end &&
            /^[0-9]/.test(next.text) &&
            (prev === undefined || prev.end < t.at || this.pos === 0));
    }
    /** A year, a decade or a century at the current token, or null when none starts here. */
    yearValue() {
        const valueAt = this.atOf();
        if (this.keyword('century')) {
            this.pos++;
            const signed = this.signHere();
            if (signed)
                this.pos++;
            const n = this.number();
            if (n === null || n % 100 !== 0)
                return this.failHere();
            this.pos++;
            return { precision: 'century', year: (signed ? -n : n) + 0, signed: true };
        }
        const ordinal = ORDINAL.exec(this.word() ?? '');
        if (ordinal !== null && this.keyword('century', 1)) {
            const n = toInt(ordinal[1]);
            const year = n === null ? Number.NaN : mul(n - 1, 100);
            if (n === null || n < 1 || !Number.isSafeInteger(year))
                this.fail('unexpected_text', valueAt);
            this.pos += 2;
            return { precision: 'century', year, signed: true };
        }
        const signed = this.signHere();
        if (signed)
            this.pos++;
        const text = this.word();
        if (text !== null && DIGITS.test(text)) {
            const n = toInt(text);
            if (n === null)
                this.fail('unexpected_text');
            this.pos++;
            return { precision: 'year', year: (signed ? -n : n) + 0, signed };
        }
        const decade = DECADE.exec(text ?? '');
        if (decade !== null) {
            const n = toInt(decade[1]);
            if (n === null || n % 10 !== 0)
                this.fail('unexpected_text');
            this.pos++;
            return { precision: 'decade', year: (signed ? -n : n) + 0, signed };
        }
        if (signed)
            this.pos--;
        return null;
    }
    /** An era named after a year (its suffix, or its name), if one is there. */
    eraAfter() {
        const match = this.matchName();
        if (match.status === 'ambiguous')
            this.nameAt(match);
        if (match.status !== 'one' || match.kind !== 'era')
            return undefined;
        this.pos += match.length;
        return match.key;
    }
    /** The year after a month, season, week or day: `[era name] year [era]`, or null if absent. */
    yearExpr() {
        const start = this.pos;
        let era;
        const match = this.matchName();
        if (match.status === 'one' && match.kind === 'era') {
            era = match.key;
            this.pos += match.length;
        }
        const signed = this.signHere();
        if (signed)
            this.pos++;
        const n = this.number();
        if (n === null) {
            this.pos = start;
            return null;
        }
        this.pos++;
        const year = (signed ? -n : n) + 0;
        era ??= this.eraAfter();
        return era === undefined ? { year } : { era, year };
    }
    monthOf(key) {
        return this.calendar.months.find((m) => m.key === key);
    }
    /** A day of a month or an intercalary month (whose day number is optional), with its time. */
    dayDraft(at, day, key) {
        const month = this.monthOf(key);
        if (day !== null && (day.n < 1 || day.n > Math.max(month.commonDays, month.leapDays))) {
            this.fail('day_out_of_range', day.at);
        }
        const year = this.yearExpr();
        const time = this.time();
        const parts = {
            ...(month.intercalary ? { intercalary: key } : { month: key }),
            ...(day === null ? {} : { day: day.n }),
            ...year,
            ...time?.parts,
        };
        const draft = this.draft(at, time?.precision ?? 'day', parts, year !== null);
        draft.interNoDay = day === null && time === null;
        return draft;
    }
    /** `, 20:15`, ` 20:15`, ` at 20:15`, `, 20h` after a day; null when no time follows. */
    time() {
        let joined = false;
        if (this.punct() === ',') {
            this.pos++;
            joined = true;
        }
        if (this.keyword('at')) {
            this.pos++;
            joined = true;
        }
        const timeAt = this.atOf();
        const hourOnly = HOUR.exec(this.word() ?? '');
        let hour = null;
        let minute = null;
        if (hourOnly !== null) {
            hour = toInt(hourOnly[1]);
            this.pos++;
        }
        else {
            const h = this.number();
            const colon = this.tok(1);
            const m = this.tok(2);
            const tight = colon?.kind === 'punct' &&
                colon.text === ':' &&
                m?.kind === 'word' &&
                colon.at === this.tok().end &&
                m.at === colon.end;
            if (h !== null && tight && DIGITS.test(m.text)) {
                hour = h;
                minute = toInt(m.text);
                this.pos += 3;
            }
            else if (joined) {
                return this.failHere();
            }
            else {
                return null;
            }
        }
        const c = this.calendar;
        if (hour === null || hour >= c.hoursPerDay || (minute !== null && minute >= c.minutesPerHour)) {
            this.fail('time_out_of_range', timeAt);
        }
        return minute === null
            ? { precision: 'hour', parts: { hour } }
            : { precision: 'minute', parts: { hour, minute: minute } };
    }
    weekDraft(at) {
        this.pos++;
        const weekAt = this.atOf();
        const week = this.number();
        if (week === null)
            return this.failHere();
        this.pos++;
        if (!this.keyword('of')) {
            if (this.tok() === undefined)
                this.fail('missing_year');
            this.failHere();
        }
        this.pos++;
        let month = null;
        const match = this.matchName();
        if (match.status === 'one' && match.kind !== 'era') {
            if (match.kind !== 'month')
                this.failHere();
            month = this.monthOf(match.key);
            this.pos += match.length;
        }
        const longest = month === null
            ? Math.max(this.calendar.commonYearDays, this.calendar.leapYearDays)
            : Math.max(month.commonDays, month.leapDays);
        if (week < 1 || mul(week - 1, this.calendar.weekdays.length) >= longest) {
            this.fail('week_out_of_range', weekAt);
        }
        const year = this.yearExpr();
        if (month === null && year === null)
            this.failHere();
        const parts = {
            ...(month === null ? {} : { month: month.key }),
            week,
            ...year,
        };
        return this.draft(at, 'week', parts, year !== null);
    }
    // -------------------------------------------------------------------------------- combining
    fillFromNow(draft) {
        if (draft.hasYear)
            return;
        if (!DAYISH.has(draft.precision))
            this.fail('missing_year', draft.end);
        const era = this.now === null ? null : eraOf(this.calendar, this.now);
        if (era === null)
            this.fail('now_unset', draft.end);
        draft.parts.era = era.key;
        draft.parts.year = era.year;
        draft.hasYear = true;
    }
    /** An intercalary month without a day number is its month when it lasts several days that year. */
    finish(draft) {
        const key = draft.parts.intercalary;
        if (!draft.interNoDay || key === undefined)
            return;
        const era = eraByKey(this.calendar, draft.parts.era ?? this.calendar.defaultEra.key);
        const year = absoluteYearOf(era ?? this.calendar.defaultEra, draft.parts.year);
        if (!Number.isSafeInteger(year))
            return;
        if (monthLength(this.monthOf(key), yearIsLeap(this.calendar, year)) > 1) {
            draft.precision = 'month';
        }
    }
    combine(left, right, open, qualifier) {
        if (right === null) {
            this.fillFromNow(left);
            this.finish(left);
            const parts = left.parts;
            return {
                kind: 'point',
                loParts: open === 'lo' ? {} : parts,
                hiParts: open === null ? null : open === 'lo' ? parts : {},
                precision: left.precision,
                qualifier,
            };
        }
        this.fillFromNow(right);
        this.finish(right);
        const key = right.parts.month ?? right.parts.intercalary;
        if (left.bare && right.precision === 'day' && key !== undefined) {
            // "12 – 15 Ember 1042": the lone number is a day, and takes its month and year from the right.
            const day = left.parts.year;
            const month = this.monthOf(key);
            if (day < 1 || day > Math.max(month.commonDays, month.leapDays)) {
                this.fail('day_out_of_range', left.at);
            }
            const { part } = left.parts;
            const { part: _, ...rest } = right.parts;
            left.parts = { ...rest, day, ...(part === undefined ? {} : { part }) };
            left.precision = 'day';
        }
        if (!left.hasYear) {
            // "12 Ember – 3 Mist 1042": the missing year and its era come from the right side.
            left.parts.year = right.parts.year;
            if (right.parts.era !== undefined)
                left.parts.era = right.parts.era;
            left.hasYear = true;
        }
        this.finish(left);
        // A one-day intercalary month is the same span at month and day precision (ruling 18), so it
        // sits beside a month side: "Reaping 1042 – Lanternnight 1042".
        for (const [a, b] of [
            [left, right],
            [right, left],
        ]) {
            if (a.interNoDay && a.precision === 'day' && b.precision === 'month')
                a.precision = 'month';
        }
        if (left.precision !== right.precision)
            this.fail('mixed_precision', right.at);
        return {
            kind: 'point',
            loParts: left.parts,
            hiParts: right.parts,
            precision: left.precision,
            qualifier,
        };
    }
}
/**
 * Reads a When phrase in the calendar's own words. Never throws; without a compiled calendar the
 * result is `invalid` with `calendar_invalid` at 0.
 */
export function parse(input, context) {
    const calendar = context?.calendar;
    if (!isCompiled(calendar))
        return { kind: 'invalid', reason: 'calendar_invalid', at: 0 };
    if (typeof input !== 'string')
        return { kind: 'invalid', reason: 'empty', at: 0 };
    const rawNow = context.now;
    const now = typeof rawNow === 'number' && Number.isSafeInteger(rawNow) ? rawNow : null;
    try {
        return new Parser(input, calendar, now).run();
    }
    catch (error) {
        if (error instanceof ParseFailure)
            return { kind: 'invalid', reason: error.reason, at: error.at };
        throw error;
    }
}
//# sourceMappingURL=parse.js.map